import { Injectable, inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { catchError, endWith, filter, map, of, switchMap, takeUntil, withLatestFrom } from 'rxjs'

import { AiArchitectUtils } from '@flowx/designer/modules/new-ai/agents/architect/services/architect.service'
import { ProcessDataModelActions } from '@flowx/designer/shared/store/actions/process-data-model.actions'
import { ProjectDataModelActions } from '@flowx/designer/shared/store/actions/project-data-model.actions'
import { selectCurrentResourceId } from '@flowx/designer/shared/store/selectors/data-model.selectors'

// import { DataModelBuilderService } from '@flowx/designer/modules/data-model-builder/services/data-model-builder.service'
// import { selectDataModelEntitiesArrayForAI } from '@flowx/designer/shared/store/selectors/data-model.selectors'
import { AiArchitectActions } from './architect.actions'

// eslint-disable-next-line import/no-cycle
import { AiActions } from '../../../store/ai.actions'
import { AiSelectors } from '../../../store/ai.selectors'
import {
  AiArchitectAPI,
  GenerateDataModelRequest,
  GenerateDocTemplateRequest,
  GenerateEnumerationRequest,
  GenerateImageRequest,
  GenerateNotTemplateRequest,
  GenerateSubstTagsRequest,
  RequestArchitectPayload,
} from '../api/architect.api'

@Injectable()
export class AiArchitectEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #aiArchitectApiService = inject(AiArchitectAPI)
  #aiArchitectUtils = inject(AiArchitectUtils)

  generateEnumeration = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiArchitectActions.generateEnumeration),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.requestId),
        this.#store.select(AiSelectors.currentEnumeration)
      ),
      map(
        ([{ prompt }, sessionId, requestId, currentEnumeration]) =>
          ({
            question: prompt,
            context: currentEnumeration,
            session_id: sessionId,
            request_id: requestId,
          }) satisfies GenerateEnumerationRequest
      ),
      switchMap((payload) =>
        this.#aiArchitectApiService.generateEnumeration(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiArchitectActions.generateEnumerationSuccess({ response })),
          catchError(({ status, error }) =>
            of(
              AiArchitectActions.generateEnumerationFailure({ statusCode: status, errorMsg: error })
            )
          )
        )
      )
    )
  )

  generateImage = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiArchitectActions.generateImage),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.requestId)
      ),
      map(
        ([{ prompt }, sessionId, requestId]) =>
          ({
            question: prompt,
            session_id: sessionId,
            request_id: requestId,
          }) satisfies GenerateImageRequest
      ),
      switchMap((payload) =>
        this.#aiArchitectApiService.generateImage(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiArchitectActions.generateImageSuccess({ response })),
          catchError(({ status, error }) =>
            of(AiArchitectActions.generateImageFailure({ statusCode: status, errorMsg: error }))
          )
        )
      )
    )
  )

  translateSubstitutionTags = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiArchitectActions.translateSubstitutionTags),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.requestId),
        this.#store.select(AiSelectors.currentEnumeration)
      ),
      map(
        ([{ prompt }, sessionId, requestId, currentEnumeration]) =>
          ({
            question: prompt,
            context: currentEnumeration,
            session_id: sessionId,
            request_id: requestId,
          }) satisfies GenerateSubstTagsRequest
      ),
      switchMap((payload) =>
        this.#aiArchitectApiService.translateSubstitutionTags(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiArchitectActions.translateSubstTagsSuccess({ response })),
          catchError(({ status, error }) =>
            of(
              AiArchitectActions.translateSubstTagsFailure({ statusCode: status, errorMsg: error })
            )
          )
        )
      )
    )
  )

  generateDocTemplates = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiArchitectActions.generateDocTemplate),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.requestId),
        this.#store.select(AiSelectors.currentNotification)
      ),
      map(
        ([{ prompt }, sessionId, requestId, currentNotification]) =>
          ({
            question: prompt,
            context: currentNotification,
            session_id: sessionId,
            request_id: requestId,
          }) satisfies GenerateDocTemplateRequest
      ),
      switchMap((payload) =>
        this.#aiArchitectApiService.generateDocumentTemplate(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiArchitectActions.generateDocTemplateSuccess({ response })),
          catchError(({ status, error }) =>
            of(
              AiArchitectActions.generateDocTemplateFailure({ statusCode: status, errorMsg: error })
            )
          )
        )
      )
    )
  )

  onDataModelDraftSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.getAnswerStreamCompleted),
      withLatestFrom(
        this.#store.select(AiSelectors.generatedDataModel),
        this.#store.select(selectCurrentResourceId),
        this.#store.select(AiSelectors.pageLocation)
      ),
      filter(([, generatedDataModel]) => !!generatedDataModel),
      map(([, generatedDataModel, processResourceId, pageLocation]) => {
        console.log(
          '[AI Architect] onDataModelDraftSuccess fired, pageLocation:',
          pageLocation,
          'generatedDataModel:',
          !!generatedDataModel
        )
        if (generatedDataModel) {
          if (pageLocation?.includes('PROJECT_DATA_MODEL')) {
            // Transform AI entities to DataModelUiEntity format for project preview
            const aiEntities = generatedDataModel.entities || []
            const projectEntities = aiEntities.map((e: any, idx: number) => ({
              id: `ai-${idx}`,
              identifier: `ai-${idx}`,
              name: e.en || e.ec || `Entity_${idx}`,
              flowxUuid: `ai-gen-${idx}`,
              resourceId: `ai-gen-${idx}`,
              resourceDefinitionId: `ai-gen-${idx}`,
              type: 'GENERATED',
              attributes: (e.fl || []).map((f: any, fidx: number) => ({
                id: `ai-attr-${idx}-${fidx}`,
                name: f.fn,
                type: f.ft || 'STRING',
                multiInstance: !!f.fa,
                description: f.fd || '',
              })),
            }))
            console.log(
              '[AI Architect] Dispatching loadProjectEntitiesGenerated with',
              projectEntities.length,
              'entities'
            )
            return ProjectDataModelActions.loadProjectEntitiesGenerated({
              entities: projectEntities,
            })
          }
          console.log(
            '[AI Architect] Dispatching loadEntitiesGenerated for process:',
            processResourceId
          )
          return ProcessDataModelActions.loadEntitiesGenerated({
            resourceId: processResourceId,
          })
        }
      })
    )
  )

  onDiscardTemporaryDataModel = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiArchitectActions.discardDraft),
      withLatestFrom(
        this.#store.select(selectCurrentResourceId),
        this.#store.select(AiSelectors.pageLocation)
      ),
      switchMap(([, processResourceId, pageLocation]) => {
        if (pageLocation?.includes('PROJECT_DATA_MODEL')) {
          return of(
            ProcessDataModelActions.discardTemporaryDataModel(),
            ProjectDataModelActions.loadProjectDataModel()
          )
        }
        return of(
          ProcessDataModelActions.discardTemporaryDataModel(),
          ProcessDataModelActions.loadEntities({ resourceId: processResourceId })
        )
      })
    )
  )

  keepDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiArchitectActions.keepDraft),
      withLatestFrom(
        this.#store.select(selectCurrentResourceId),
        this.#store.select(AiSelectors.pageLocation)
      ),
      switchMap(([, processResourceId, pageLocation]) => {
        if (pageLocation?.includes('PROJECT_DATA_MODEL')) {
          return of(ProjectDataModelActions.keepProjectTemporaryDataModel())
        }
        return of(ProcessDataModelActions.keepTemporaryDataModel({ resourceId: processResourceId }))
      })
    )
  )

  generateDataModelStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiArchitectActions.generateDataModelStream),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.requestId),
        this.#store.select(AiSelectors.currentPlanStep)
        // this.#store.select(selectDataModelEntitiesArrayForAI)
      ),
      map(
        ([{ prompt }, sessionId, requestId, currentPlanStep]) =>
          ({
            question: prompt,
            action: 'generate_data_model',
            context: null, // selectDataModelEntitiesArrayForAI,
            session_id: sessionId,
            request_id: requestId,
            skip_greeting: !!currentPlanStep,
          }) satisfies RequestArchitectPayload
      ),
      switchMap((payload) =>
        this.#aiArchitectApiService.getSSEGenerateArchitect(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiArchitectActions.getAnswerEventStream({ response })),
          catchError(({ status, error }) =>
            of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: error }))
          ),
          endWith(AiActions.getAnswerStreamCompleted())
        )
      )
    )
  )

  generateDataModel = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiArchitectActions.generateDataModel),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.requestId)
        // this.#store.select(selectDataModelEntitiesArrayForAI)
      ),
      map(
        ([{ prompt }, sessionId, requestId]) =>
          ({
            question: prompt,
            context: null, // selectDataModelEntitiesArrayForAI,
            session_id: sessionId,
            request_id: requestId,
          }) satisfies GenerateDataModelRequest
      ),
      switchMap((payload) =>
        this.#aiArchitectApiService.generateDataModel(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiArchitectActions.generateDataModelSuccess({ response })),
          catchError(({ status, error }) =>
            of(AiArchitectActions.generateDataModelFailure({ statusCode: status, errorMsg: error }))
          )
        )
      )
    )
  )

  generateNotTemplates = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiArchitectActions.generateNotTemplate),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.requestId),
        this.#store.select(AiSelectors.currentNotification)
      ),
      map(
        ([{ prompt }, sessionId, requestId, currentNotification]) =>
          ({
            question: prompt,
            context: currentNotification,
            session_id: sessionId,
            request_id: requestId,
          }) satisfies GenerateNotTemplateRequest
      ),
      switchMap((payload) =>
        this.#aiArchitectApiService.generateNotificationTemplate(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiArchitectActions.generateNotTemplateSuccess({ response })),
          catchError(({ status, error }) =>
            of(
              AiArchitectActions.generateNotTemplateFailure({ statusCode: status, errorMsg: error })
            )
          )
        )
      )
    )
  )

  sendFeedback = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiArchitectActions.sendFeedback),
        switchMap(({ payload }) => this.#aiArchitectApiService.sendFeedback(payload))
      ),
    { dispatch: false }
  )
}
